import type { ResellerOverview } from "@xop-any-time/shared";
import { useQuery } from "@tanstack/react-query";
import type { JSX, ReactNode } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";

import { AcceptInvitationPage } from "./pages/AcceptInvitationPage.js";
import { NetworkPage } from "./pages/account/NetworkPage.js";
import { ProfilePage } from "./pages/account/ProfilePage.js";
import { TeamPage } from "./pages/account/TeamPage.js";
import { RequireApplicant } from "./auth/RequireApplicant.js";
import { RequireAuth } from "./auth/RequireAuth.js";
import { RequirePermission } from "./auth/RequirePermission.js";
import { SessionExpiryBridge } from "./auth/SessionExpiryBridge.js";
import { CrmRoot } from "./crm/CrmRoot.js";
import CrmCustomersPage from "./crm/pages/customers/CustomersPage.js";
import CrmCustomerDetailPage from "./crm/pages/customers/detail/CustomersDetailPage.js";
import CrmContactsPage from "./crm/pages/contacts/ContactsPage.js";
import { ResellerProspectsPage } from "./crm/pages/prospects/ResellerProspectsPage.js";
import CrmProspectDetailPage from "./crm/pages/prospects/detail/ProspectsDetailPage.js";
import CrmInvoicesPage from "./crm/pages/invoices/InvoicesPage.js";
import CrmInvoiceDetailPage from "./crm/pages/invoices/detail/InvoicesDetailPage.js";
import { getOverview, keys } from "./lib/crmApi.js";
import { LoginPage } from "./pages/LoginPage.js";
import { RegisterPage } from "./pages/RegisterPage.js";
import { OnboardingPage } from "./pages/onboarding/OnboardingPage.js";
import { HomeRoute } from "./pages/public/HomeRoute.js";
import { PublicLayout } from "./pages/public/PublicLayout.js";
import { PrivacyPage } from "./pages/public/PrivacyPage.js";
import { ResellerProgramPage } from "./pages/public/ResellerProgramPage.js";
import { TermsPage } from "./pages/public/TermsPage.js";
import { ContactPage } from "./pages/portal/contact/ContactPage.js";
import { DocumentsPage } from "./pages/portal/documents/DocumentsPage.js";
import { ReleaseDetailPage } from "./pages/portal/releases/ReleaseDetailPage.js";
import { ReleasesPage } from "./pages/portal/releases/ReleasesPage.js";
import { TrainingsPage } from "./pages/portal/trainings/TrainingsPage.js";
import { ModulePlayerPage } from "./pages/portal/trainings/ModulePlayerPage.js";
import { MODULES_PATH } from "./pages/portal/trainings/module-player.js";
import { TRAININGS_PATH } from "./pages/portal/trainings/training-paths.js";
import { ReleaseDocumentsIndexPage } from "./pages/knowledge-base/ReleaseDocumentsIndexPage.js";
import { ReleaseDocumentsReleasePage } from "./pages/knowledge-base/ReleaseDocumentsReleasePage.js";
import { ArticlePage } from "./pages/knowledge-base/ArticlePage.js";
import { CategoryPage } from "./pages/knowledge-base/CategoryPage.js";
import { KnowledgeBasePage } from "./pages/knowledge-base/KnowledgeBasePage.js";
import { LegacyDocumentationRedirect } from "./pages/knowledge-base/legacy-redirects.js";
import { AppShell } from "./pages/portal/AppShell.js";
import { CustomerLicenceCard } from "./pages/portal/licences/CustomerLicenceCard.js";
import { LicenceBuilderPage } from "./pages/portal/licences/LicenceBuilderPage.js";
import { LicencesPage } from "./pages/portal/licences/LicencesPage.js";
import { OverviewPage } from "./pages/portal/OverviewPage.js";
import { NewPurchaseOrderPage } from "./pages/portal/purchase-orders/NewPurchaseOrderPage.js";
import { PurchaseOrderDetailPage } from "./pages/portal/purchase-orders/PurchaseOrderDetailPage.js";
import { PurchaseOrdersPage } from "./pages/portal/purchase-orders/PurchaseOrdersPage.js";
import { QuoteDetailPage } from "./pages/portal/quotes/QuoteDetailPage.js";
import { QuotesPage } from "./pages/portal/quotes/QuotesPage.js";
import { RequestQuotePage } from "./pages/portal/quotes/RequestQuotePage.js";

/**
 * Everything behind the sign-in wall shares the command center's chrome, so
 * the guard and the shell are applied together rather than repeated per
 * route.
 *
 * The customer count for the rail's badge rides the SAME react-query cache
 * entry the overview page populates, so the badge costs no extra request on
 * any page reached from the overview.
 */
function Portal({ children }: { children: ReactNode }): JSX.Element {
  const { data } = useQuery({
    queryKey: keys.overview,
    queryFn: getOverview,
    // The rail is chrome — a failed count must never surface an error, it
    // just renders no chip.
    retry: false,
  });

  return (
    <RequireAuth>
      <AppShell
        counts={{
          customers: data?.customerCount ?? null,
          prospects: data?.prospectCount ?? null,
          // The Invoices chip is the UNPAID count (2026-09-15) — what needs
          // chasing, not the size of the archive. Same overview call, so it
          // costs nothing extra.
          invoices: data?.unpaidInvoiceCount ?? null,
        }}
      >
        {children}
      </AppShell>
    </RequireAuth>
  );
}

interface CrmLocation {
  pathname: string;
  search: string;
  hash: string;
}

/**
 * Sends `/crm/<anything>` to `/<anything>`.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * WHY THIS EXISTS — it is the fix for "clicking an invoice does nothing".
 * ─────────────────────────────────────────────────────────────────────────
 * In xop-edge-staff the CRM is a sub-app mounted at `/crm`, so every ported
 * component navigates with that prefix hardcoded: `navigate('/crm/invoices/'
 * + id)` in `document-list.tsx`, `InvoicesPage.tsx`,
 * `customer-invoices-table.tsx`, `CustomersPage.tsx`. This portal mounts the
 * same pages at the ROOT, because here they are not a sub-app — Customers,
 * Invoices and Quotes are top-level sections of the product, and the nav rail
 * presents them that way.
 *
 * `/crm/quotes/:id` is now a DEAD END, deliberately. The ported components
 * still navigate there for a quote row, and this redirect still strips the
 * prefix — but `/quotes/:id` is the portal's own XOP-quote page since spec
 * 2026-09-09 §3.2, so such a link lands on "That quote could not be found".
 * Do not repoint it at `/invoices/:id`: `InvoicesDetailPage` hardcodes
 * `document_type: 'invoice'`, so it would print a quote under an INVOICE
 * heading — a wrong document rather than a missing one.
 *
 * The result was that every "View" click navigated to a path matching no
 * route, fell through to the catch-all below, and quietly landed the user back
 * on the overview. It looked like the PDF failed to render; nothing had even
 * tried to render one.
 *
 * The alternative was editing the prefix out of five ported files, which is
 * the one thing `src/crm/README.md` asks nobody to do — those files are kept
 * diffable against their source so re-syncing stays a `diff -r`. One route
 * that translates the mount point is cheaper than five edits that have to be
 * re-applied on every sync, and it covers ported paths nobody has hit yet.
 *
 * `replace` so the bounce leaves no history entry: Back from an invoice must
 * return to the list, not to the redirect that sent you there.
 */
export function stripCrmPrefix({ pathname, search, hash }: CrmLocation): string {
  // A bare `/crm` strips to "", which is not a path — `page-permission-guard`
  // sends a refused page there, and the overview is the right landing.
  const target = pathname.replace(/^\/crm/, "") || "/";
  // The query is NOT decoration: `…/generate?invoiceId=31&customerId=4` is how
  // the ported code says "edit THIS invoice". Dropping it would quietly turn
  // that into "start a blank one".
  return `${target}${search}${hash}`;
}

function CrmPrefixRedirect(): JSX.Element {
  return <Navigate to={stripCrmPrefix(useLocation())} replace />;
}

/**
 * A CRM page ported from xop-edge-staff, inside this portal's chrome.
 *
 * `CrmRoot` supplies the Redux store those pages read their identity from —
 * see that file for why the store is seeded rather than the pages rewritten,
 * and why the permissions it seeds grant nothing.
 */
function Crm({ children }: { children: ReactNode }): JSX.Element {
  return (
    <Portal>
      <CrmRoot>{children}</CrmRoot>
    </Portal>
  );
}

export function App(): JSX.Element {
  return (
    <>
      {/* Renders nothing. It is here, above the routes rather than in
          `main.tsx`, because it is the app's answer to "the session is gone":
          `src/crm/lib/api.ts` reports a 401 from the CRM passthrough and this
          turns it into a router navigation that keeps the reseller's place.
          Inside `App` so it cannot drift away from the routes it navigates
          to, and so every test that renders `App` gets the real wiring. */}
      <SessionExpiryBridge />
      <Routes>
        {/* PUBLIC. The landing page and the Reseller Program are the
            marketing site (spec `docs/superpowers/specs/2026-09-08-public-site-design.md`):
            the sign-in screen's chrome (`PublicLayout`) plus the site
            navigation, reached by anyone, signed in or not.

            `/login` and `/register` sit inside that same chrome now — one top
            bar and footer for the whole public site, not a bespoke pair per
            page. `/accept-invitation` does NOT: it especially cannot sit
            behind `RequireAuth`, since an unaccepted invite leaves
            `password_set_at` nil, so portal-api refuses the account until the
            password is set on this very page — guarding it would make the
            invitation email a link to a sign-in the invitee is not yet able to
            pass. It also skips `PublicLayout`: it is a task with one job, not
            a page someone browses to from the marketing nav.

            `/` is auth-aware rather than a plain public route: `HomeRoute`
            decides per visitor whether "/" means the landing page, the
            signed-in portal, or the onboarding wall — see that file. */}
        <Route path="/login" element={<PublicLayout><LoginPage /></PublicLayout>} />
        <Route path="/accept-invitation" element={<AcceptInvitationPage />} />
        <Route path="/register" element={<PublicLayout><RegisterPage /></PublicLayout>} />
        <Route path="/reseller-program" element={<PublicLayout><ResellerProgramPage /></PublicLayout>} />
        {/* The legal pages (2026-09-14) — public, linked from every footer. */}
        <Route path="/terms" element={<PublicLayout><TermsPage /></PublicLayout>} />
        <Route path="/privacy" element={<PublicLayout><PrivacyPage /></PublicLayout>} />

        {/* The onboarding wall (spec E §10.2). NO <Portal>, NO <AppShell>: an
            applicant has no rail, no counts and no avatar menu offering /team. */}
        <Route
          path="/onboarding"
          element={
            <RequireApplicant>
              <OnboardingPage />
            </RequireApplicant>
          }
        />

        {/* This portal's own screens. `/` is handled by `HomeRoute`, not a
            bare `<Portal>`: it is the one path whose page depends on who is
            asking (spec §3). */}
        <Route path="/" element={<HomeRoute portal={<Portal><OverviewPage /></Portal>} />} />
        {/* Settings is GONE (2026-09-14). A reseller issues no documents of its
            own any more, so there is no branding, no payment identity and no
            document footer to set — the page, its preview, the BFF writes and
            the `settings:*` permission keys were all deleted. The two paths
            stay only as redirects for old bookmarks. */}
        <Route path="/settings" element={<Navigate to="/" replace />} />
        <Route path="/branding" element={<Navigate to="/" replace />} />

        {/* Legal — the company's OWN paperwork (2026-09-13; "Documents" until
            2026-09-17): the agreements it signed with XOP Networks and its
            file cabinet. NO permission gate: every signed-in member of the
            company sees it, even before anything is filed (CEO, 2026-09-17) —
            the page says so rather than hiding. crm-api still scopes every
            read to the member's OWN company; filing and removing need
            `documents:edit`. `/documents` stays as a redirect for bookmarks. */}
        <Route path="/legal" element={<Portal><DocumentsPage /></Portal>} />
        <Route path="/documents" element={<Navigate to="/legal" replace />} />

        {/* Account surfaces (spec D). Reached from the avatar menu rather than
            the rail: they are about the PERSON and the COMPANY, not about the
            work, and the rail is the command center's and lists the work.

            `/team` and `/network` are NOT guarded by role here. The menu hides
            their entries for a salesperson, but a route guard would be a second
            place that decides authorisation — and it would decide from the
            session cookie, which is stale for hours after a demotion. Both pages
            ask the server, render what it says, and explain a refusal (see each
            page's error branch). One authority: crm-api. */}
        {/* Contact XOP Networks (2026-09-15) — sales, support, headquarters.
            Reached from the foot of the rail, which used to print all of it.
            Every signed-in person may contact XOP, so no permission gate. */}
        <Route path="/contact" element={<Portal><ContactPage /></Portal>} />
        <Route path="/profile" element={<Portal><ProfilePage /></Portal>} />
        <Route path="/team" element={<Portal><TeamPage /></Portal>} />
        <Route path="/network" element={<Portal><NetworkPage /></Portal>} />

        {/* Documentation — the LIBRARY shelf of XOP's documentation, over the
            folders staff opened to this company: a landing page (search,
            folders, featured, recent), a folder, an article. Three sibling
            routes and no layout: there is no sidebar tree to keep mounted.
            The article route is declared AFTER the folder route so "category"
            can never be read as a slug. (Called "Knowledge Base" and served
            at `/knowledge-base/*` from 2026-09-14 to 2026-09-17.)

            Release documentation is NOT here — a guide pinned to a version is
            read on Releases, under that version. Search still finds it.

            There is no authoring branch, unlike the command center's
            equivalent: portal-api's reseller namespace exposes no write, so
            there is nothing for a /manage route to call. */}
        <Route
          path="/documentation"
          element={
            <Portal>
              <RequirePermission permission="documentation:view">
                <KnowledgeBasePage />
              </RequirePermission>
            </Portal>
          }
        />
        <Route
          path="/documentation/category/:slug"
          element={
            <Portal>
              <RequirePermission permission="documentation:view">
                <CategoryPage />
              </RequirePermission>
            </Portal>
          }
        />
        {/* Release documents (2026-09-17): the pinned folder on the landing
            page — every release, then one release's versions and each
            version's documents (the same list the Releases page shows).
            Static segments, so they outrank `/documentation/:slug` below; an
            article whose slug is literally "releases" is shadowed. */}
        <Route
          path="/documentation/releases"
          element={
            <Portal>
              <RequirePermission permission="documentation:view">
                <ReleaseDocumentsIndexPage />
              </RequirePermission>
            </Portal>
          }
        />
        <Route
          path="/documentation/releases/:release"
          element={
            <Portal>
              <RequirePermission permission="documentation:view">
                <ReleaseDocumentsReleasePage />
              </RequirePermission>
            </Portal>
          }
        />
        <Route
          path="/documentation/:slug"
          element={
            <Portal>
              <RequirePermission permission="documentation:view">
                <ArticlePage />
              </RequirePermission>
            </Portal>
          }
        />
        {/* Old URLs, all redirected by ONE table (`legacy-redirects.tsx`):
            the pre-2026-09-14 shapes (`/documentation/search`, `/type/…`,
            `/library/…`, `/release/…` — static segments outrank `:slug`, so
            they are matched before the article route) and every
            `/knowledge-base/*` URL of 2026-09-14…17. */}
        <Route path="/documentation/search" element={<LegacyDocumentationRedirect />} />
        <Route path="/documentation/library/:slug" element={<LegacyDocumentationRedirect />} />
        <Route path="/documentation/type/:slug/*" element={<LegacyDocumentationRedirect />} />
        <Route path="/documentation/release/:id" element={<LegacyDocumentationRedirect />} />
        <Route path="/documentation/*" element={<LegacyDocumentationRedirect />} />
        <Route path="/knowledge-base/*" element={<LegacyDocumentationRedirect />} />

        {/* Releases (2026-09-14) — the command center's CRM ▸ Releases pages,
            read-only: a folder per release, and a release's own page with a
            rail of its versions, each with its download tiles, change log and
            documentation. A reseller downloads documents and disks and
            changes nothing. Gated on `documentation:view`, "may read what XOP
            publishes". `/downloads` was this section until today and lands
            here for old bookmarks. */}
        <Route
          path="/releases"
          element={
            <Portal>
              <RequirePermission permission="documentation:view">
                <ReleasesPage />
              </RequirePermission>
            </Portal>
          }
        />
        <Route
          path="/releases/:release"
          element={
            <Portal>
              <RequirePermission permission="documentation:view">
                <ReleaseDetailPage />
              </RequirePermission>
            </Portal>
          }
        />
        <Route path="/downloads" element={<Navigate to="/releases" replace />} />

        {/* Trainings (2026-09-17) — the PDFs and videos staff attach to a
            release as TRAINING, on a page of their own rather than on the
            release (CEO). Grouped by release then version; a row opens the
            ordinary article reader with Back → Trainings. Gated on
            `documentation:view` like Releases and Documentation. */}
        <Route
          path={TRAININGS_PATH}
          element={
            <Portal>
              <RequirePermission permission="documentation:view">
                <TrainingsPage />
              </RequirePermission>
            </Portal>
          }
        />
        {/* An Online Training MODULE (2026-11-02): the video player (three
            quarters) beside the module's topic menu (one quarter), with
            Previous / Next under the video. `?topic=` names the open video. */}
        <Route
          path={`${MODULES_PATH}/:slug`}
          element={
            <Portal>
              <RequirePermission permission="documentation:view">
                <ModulePlayerPage />
              </RequirePermission>
            </Portal>
          }
        />
        <Route
          path={`${TRAININGS_PATH}/:slug`}
          element={
            <Portal>
              <RequirePermission permission="documentation:view">
                <ArticlePage back={{ to: TRAININGS_PATH, label: "Online Training" }} />
              </RequirePermission>
            </Portal>
          }
        />

        {/* The command center's CRM, verbatim. Customers, contacts and invoices
            are ITS pages — the same tables, filters, forms, modals and PDF
            generator staff use — reading through this portal's BFF and bounded
            by crm-api's reseller wall.

            QUOTES ARE NOT AMONG THEM ANY MORE. `/quotes` was the command
            center's quote builder: the reseller's own quotes to its own
            customers. Spec 2026-09-09 §3.2 replaced that section with XOP
            Networks' quotes TO the reseller, which is a portal-native page
            (`pages/portal/quotes/`), and a reseller's own customer documents
            now live with each customer in the file manager. The portal offers
            no customer-facing quotes at all: no section, and no route that
            opens the ported editor as a quote. Pre-existing customer quotes
            stay in the command center for XOP staff.

            The `/invoices/:id` route is the DOCUMENT page: it fetches crm-api's
            rendered PDF (`lib/documentPdf.ts` — the portal draws no document)
            and shows it inline through `<PdfReader>` (pdf.js → canvas). */}
        <Route
          path="/customers"
          element={
            <Crm>
              <RequirePermission permission="customers:view">
                <CrmCustomersPage />
              </RequirePermission>
            </Crm>
          }
        />
        {/* The licence card is composed BESIDE the ported detail page, not into
            it. `src/crm/README.md` asks that ported files stay diffable against
            xop-edge-staff so re-syncing is a `diff -r`, and a reseller-only
            licence panel is exactly the kind of invention that rule is about.
            One line here beats an edit that has to be re-applied every sync. */}
        <Route
          path="/customers/:id"
          element={
            <Crm>
              <RequirePermission permission="customers:view">
                <CrmCustomerDetailPage />
                <CustomerLicenceCard />
              </RequirePermission>
            </Crm>
          }
        />
        {/* Prospects (spec E §10.3, extended 2026-09-09 §4.5): the leads XOP's
            salespeople attach to this reseller in the CRM, plus what a member
            holding `prospects:edit` can create/edit/convert/delete itself —
            see src/crm/README.md for what the port removes and why. The mount
            picks the `all` origin so a lead the sales agent created is not
            filtered out of the reseller's own list. */}
        <Route
          path="/prospects"
          element={
            <Crm>
              <RequirePermission permission="prospects:view">
                <ResellerProspectsPage />
              </RequirePermission>
            </Crm>
          }
        />
        <Route
          path="/prospects/:id"
          element={
            <Crm>
              <RequirePermission permission="prospects:view">
                <CrmProspectDetailPage />
              </RequirePermission>
            </Crm>
          }
        />
        {/* The command center's AI-prospects list, which an AI-tagged prospect's
            Back button targets (`prospectListPathForLead`). One list here. */}
        <Route path="/ai-prospects" element={<Navigate to="/prospects" replace />} />
        <Route
          path="/contacts"
          element={
            <Crm>
              <RequirePermission permission="contacts:view">
                <CrmContactsPage />
              </RequirePermission>
            </Crm>
          }
        />
        <Route
          path="/invoices"
          element={
            <Crm>
              <RequirePermission permission="invoices:view">
                <CrmInvoicesPage />
              </RequirePermission>
            </Crm>
          }
        />
        {/* A RESELLER GENERATES NO INVOICES (2026-09-13). Invoices go one way
            — XOP bills the reseller, and the reseller pays — so the editor is
            not mounted and this URL lands on the list instead of 404ing a
            bookmark. What a reseller asks XOP for is a QUOTE, at
            `/quotes/request`.

            It sits above `/invoices/:id` in this file for readability only —
            react-router ranks a static segment over a dynamic one regardless
            of order, so `/invoices/generate` can never be read as a document
            with the id "generate".

            The ported editor and its document-type selector are left in the
            tree untouched — see `src/crm/README.md`: de-quoting the shared
            invoice components would turn the next re-sync against
            xop-edge-staff into archaeology. Nothing routes to them. */}
        <Route path="/invoices/generate" element={<Navigate to="/invoices" replace />} />
        <Route
          path="/invoices/:id"
          element={
            <Crm>
              <RequirePermission permission="invoices:view">
                <CrmInvoiceDetailPage />
              </RequirePermission>
            </Crm>
          }
        />
        {/* Quotes are XOP NETWORKS' quotes to this reseller (spec 2026-09-09
            §5), not the reseller's own customer-facing documents — those were
            removed from the portal in the same change, and a reseller's own
            quote to its customer now lives with that customer in the file
            manager. Portal-native, so `<Portal>` rather than `<Crm>`.

            The permission is `invoices:view` and NOT a new section: spec §3.4
            reuses the money-document keys, so a person who may see invoices may
            see the quotes XOP Networks sends, and `invoices:edit` is what
            requesting and deciding needs. */}
        <Route
          path="/quotes"
          element={
            <Portal>
              <RequirePermission permission="invoices:view">
                <QuotesPage />
              </RequirePermission>
            </Portal>
          }
        />
        {/* Asking XOP Networks for a quote. Gated on `invoices:view` like the
            list — `invoices:edit` is what the POST needs, and crm-api refuses
            it; a second route guard here would decide from the session cookie,
            which is stale for up to a minute after a demotion. The button that
            leads here is hidden without `invoices:edit` (QuotesPage). */}
        <Route
          path="/quotes/request"
          element={
            <Portal>
              <RequirePermission permission="invoices:view">
                <RequestQuotePage />
              </RequirePermission>
            </Portal>
          }
        />
        {/* One quote from XOP Networks, portal-native like the list. The
            ported customer-quote document page that used to answer this path
            is a RESELLER's quote to its own customer, which now lives with
            that customer in the file manager. */}
        <Route
          path="/quotes/:id"
          element={
            <Portal>
              <RequirePermission permission="invoices:view">
                <QuoteDetailPage />
              </RequirePermission>
            </Portal>
          }
        />

        {/* Purchase orders (2026-09-17) — this reseller's orders TO XOP
            Networks, made from an accepted quote or uploaded. Portal-native.
            Same keys as Quotes and no section of their own: `invoices:view`
            reads, `invoices:edit` adds one — the one route that is nothing BUT
            a write form carries that key too, so a viewer who follows an old
            link gets the no-access panel rather than a form crm-api refuses. */}
        <Route
          path="/purchase-orders"
          element={
            <Portal>
              <RequirePermission permission="invoices:view">
                <PurchaseOrdersPage />
              </RequirePermission>
            </Portal>
          }
        />
        <Route
          path="/purchase-orders/new"
          element={
            <Portal>
              <RequirePermission permission="invoices:edit">
                <NewPurchaseOrderPage />
              </RequirePermission>
            </Portal>
          }
        />
        <Route
          path="/purchase-orders/:id"
          element={
            <Portal>
              <RequirePermission permission="invoices:view">
                <PurchaseOrderDetailPage />
              </RequirePermission>
            </Portal>
          }
        />

        {/* Licensing. This portal's OWN screens, not a port — the command
            center has no equivalent, because staff set a customer's product and
            features through the Agreement modal rather than as a licence. The
            builder is a route rather than a modal so a reseller can link a
            colleague straight at one customer's licence. */}
        <Route
          path="/licences"
          element={
            <Portal>
              <RequirePermission permission="licences:view">
                <LicencesPage />
              </RequirePermission>
            </Portal>
          }
        />
        {/* `new` is the builder in create mode (?customer=&name=, or ?from= to
            duplicate a generated licence onto another machine). Nothing exists
            until Generate is pressed. */}
        <Route
          path="/licences/new"
          element={
            <Portal>
              <RequirePermission permission="licences:edit">
                <LicenceBuilderPage />
              </RequirePermission>
            </Portal>
          }
        />
        <Route
          path="/licences/:id"
          element={
            <Portal>
              <RequirePermission permission="licences:view">
                <LicenceBuilderPage />
              </RequirePermission>
            </Portal>
          }
        />
        {/* American spelling lands on the same page. The product spells it
            "licence" throughout, and half the codebase's own columns spell it
            "license" — a bookmark typed either way should work. */}
        <Route path="/licenses" element={<Navigate to="/licences" replace />} />

        {/* The ported CRM's OWN mount point. See <CrmPrefixRedirect> — this is
            what makes "view this invoice" arrive somewhere, and it must sit
            ABOVE the catch-all or the catch-all swallows it. */}
        <Route path="/crm/*" element={<CrmPrefixRedirect />} />

        {/* Unknown paths land on the overview rather than a 404 page: this is a
            small app behind a login, so a stale bookmark is far more likely
            than a genuinely missing route. */}
        <Route path="*" element={<Portal><OverviewPage /></Portal>} />
      </Routes>
    </>
  );
}
